import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getUserDetailsFromReducer, updateUserDetailsInReducer } from "Store/reducers/Auth/AuthSlice";
import { useFeatureAccess } from "Hooks/useFeatureAccess";
import { getAccountDetails } from "Sdk/Actions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faIdBadge,
  faCrown,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faClock,
  faChartLine,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { API } from "aws-amplify";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FetchFileFromS3 } from "Utility/Helpers/Storage";
import { toast } from "@/lib/toast";

const PLAN_LABELS = { free: "Free", essential: "Essential", pro: "Pro", trial: "Free Trial", paid: "Pro" };

function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(script);
  });
}

const PRICING = {
  INR: {
    essential: { monthly: 150, yearly: 1500 },
    pro: { monthly: 200, yearly: 2000 },
  },
  USD: {
    essential: { monthly: 20, yearly: 200 },
    pro: { monthly: 25, yearly: 250 },
  },
};

const MyAccounts = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector(getUserDetailsFromReducer);
  const {
    plan,
    userType,
    nextBillingAt,
    getUsage,
    getUsageForFeature,
  } = useFeatureAccess();

  const [logoUrl, setLogoUrl] = React.useState(null);
  const [currency, setCurrency] = React.useState("INR");
  const [subscribing, setSubscribing] = React.useState(null);

  React.useEffect(() => {
    if (user?.logo) {
      FetchFileFromS3(user.logo).then(setLogoUrl).catch(() => setLogoUrl(null));
    } else {
      setLogoUrl(null);
    }
  }, [user?.logo]);

  React.useEffect(() => {
    if (searchParams.get("subscription") === "success" && user?.username) {
      getAccountDetails(user.username).then((data) => {
        if (data) dispatch(updateUserDetailsInReducer(data));
      });
      setSearchParams((p) => {
        p.delete("subscription");
        return p;
      });
    }
  }, [searchParams, user?.username, dispatch, setSearchParams]);

  const displayPlan = plan === "trial" ? "Free Trial" : PLAN_LABELS[plan] || (userType === "paid" || userType === "lifetime" ? "Pro" : "Free");
  const isFreeTier = plan === "free" || (plan === "trial" && !nextBillingAt) || (userType === "free") || (userType === "trial" && (!nextBillingAt || new Date(nextBillingAt) <= new Date()));
  const isPaidTier = plan === "essential" || plan === "pro" || userType === "paid" || userType === "lifetime" || (userType === "trial" && nextBillingAt && new Date(nextBillingAt) > new Date());

  const usage = getUsage();
  const feedbackUsage = getUsageForFeature("feedbackViews");
  const aiReviewsUsage = getUsageForFeature("aiReviews");
  const extensionUsage = getUsageForFeature("extensionResponses");

  const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const handleSubscribe = async (planKey, billingCycle) => {
    const key = `${planKey}_${billingCycle}`;
    setSubscribing(key);
    try {
      const data = await API.post("REST", "/publish/subscription/create", {
        body: { plan: planKey, billingCycle },
      });
      const subscriptionId = data.subscriptionId || data.subscription_id;
      const razorpayKey = data.keyId || data.key_id;
      setCurrency(data.currency || currency);

      if (!subscriptionId || !razorpayKey) {
        toast.error("No payment link received.");
        return;
      }

      await loadRazorpayScript();
      const planLabel = planKey === "pro" ? "Pro" : "Essential";
      const cycleLabel = billingCycle === "yearly" ? "Yearly" : "Monthly";

      const options = {
        key: razorpayKey,
        subscription_id: subscriptionId,
        name: "InProfile",
        description: `${planLabel} Plan (${cycleLabel})`,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        modal: { ondismiss: () => setSubscribing(null) },
        handler: async () => {
          setSubscribing(null);
          toast.success("Payment successful! Activating your plan…");
          if (user?.username) {
            const refetch = () => getAccountDetails(user.username);
            await new Promise((r) => setTimeout(r, 2500));
            await refetch();
            await new Promise((r) => setTimeout(r, 2500));
            await refetch();
            toast.success("Your plan is now active.");
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        setSubscribing(null);
        toast.error("Payment failed. Please try again.");
      });
      rzp.open();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || e.response?.data?.error || e.message || "Something went wrong";
      toast.error(msg);
      setSubscribing(null);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-6">My Account</h1>

        <div className="grid gap-6">
          <Card className="squircle shadow-md">
            <CardHeader>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FontAwesomeIcon icon={faIdBadge} className="text-primary" />
                Business details
              </h2>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                {logoUrl ? (
                  <img src={logoUrl} alt="" className="w-12 h-12 rounded-full object-cover bg-muted" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-semibold">
                    {(user?.name || user?.username || "?")[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-medium text-foreground">{user?.name || "—"}</p>
                  <p className="text-sm text-muted-foreground">inprofile.co/{user?.username || "—"}</p>
                </div>
              </div>
              <div className="grid gap-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FontAwesomeIcon icon={faEnvelope} className="w-4" />
                  <span>{user?.email || "—"}</span>
                </div>
                {user?.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <FontAwesomeIcon icon={faPhone} className="w-4" />
                    <span>{user.phone}</span>
                  </div>
                )}
                {user?.address && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="w-4" />
                    <span>{user.address}</span>
                  </div>
                )}
                {user?.timing && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <FontAwesomeIcon icon={faClock} className="w-4" />
                    <span>{user.timing}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="squircle shadow-md">
            <CardHeader>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FontAwesomeIcon icon={faCrown} className="text-primary" />
                Current plan
              </h2>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">{displayPlan}</span>
                {isPaidTier && nextBillingAt && (
                  <span className="text-sm text-muted-foreground">
                    Next billing: {formatDate(nextBillingAt)}
                  </span>
                )}
              </div>
              {isFreeTier && (
                <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                  <p className="text-sm font-medium text-foreground">Usage this period</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>Feedback views: {feedbackUsage.used}/{feedbackUsage.limit ?? "—"}</li>
                    <li>AI reviews: {aiReviewsUsage.used}/{aiReviewsUsage.limit ?? "—"}</li>
                    <li>Extension responses: {extensionUsage.used}/{extensionUsage.limit ?? "—"}</li>
                  </ul>
                  {usage.periodEndAt && (
                    <p className="text-xs text-muted-foreground pt-1">
                      Resets on {formatDate(usage.periodEndAt)}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {isFreeTier && (
            <Card className="squircle shadow-md">
              <CardHeader>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faChartLine} className="text-primary" />
                  Upgrade plan
                </h2>
                <p className="text-sm text-muted-foreground">
                  Pricing in {currency}. Currency is set by your region.
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="border border-border rounded-xl p-4 flex flex-col">
                    <p className="font-semibold text-foreground">Essential</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Unlimited feedback views, AI review writing, full profile builder, 30 extension responses/month.
                    </p>
                    <div className="mt-4 text-lg font-bold text-primary">
                      {currency === "INR"
                        ? `₹${PRICING.INR.essential.monthly}/mo · ₹${PRICING.INR.essential.yearly}/yr`
                        : `$${PRICING.USD.essential.monthly}/mo · $${PRICING.USD.essential.yearly}/yr`}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        className="squircle"
                        disabled={!!subscribing}
                        onClick={() => handleSubscribe("essential", "monthly")}
                      >
                        {subscribing === "essential_monthly" ? <FontAwesomeIcon icon={faSpinner} spin /> : "Subscribe monthly"}
                      </Button>
                      <Button
                        variant="outline"
                        className="squircle"
                        disabled={!!subscribing}
                        onClick={() => handleSubscribe("essential", "yearly")}
                      >
                        {subscribing === "essential_yearly" ? <FontAwesomeIcon icon={faSpinner} spin /> : "Subscribe yearly"}
                      </Button>
                    </div>
                  </div>
                  <div className="border border-primary rounded-xl p-4 flex flex-col ring-1 ring-primary">
                    <p className="font-semibold text-foreground">Pro</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Everything in Essential + AI summary, feedback dashboard, reviews dashboard, AI GEO/SEO, unlimited extension.
                    </p>
                    <div className="mt-4 text-lg font-bold text-primary">
                      {currency === "INR"
                        ? `₹${PRICING.INR.pro.monthly}/mo · ₹${PRICING.INR.pro.yearly}/yr`
                        : `$${PRICING.USD.pro.monthly}/mo · $${PRICING.USD.pro.yearly}/yr`}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        className="squircle"
                        disabled={!!subscribing}
                        onClick={() => handleSubscribe("pro", "monthly")}
                      >
                        {subscribing === "pro_monthly" ? <FontAwesomeIcon icon={faSpinner} spin /> : "Subscribe monthly"}
                      </Button>
                      <Button
                        variant="outline"
                        className="squircle"
                        disabled={!!subscribing}
                        onClick={() => handleSubscribe("pro", "yearly")}
                      >
                        {subscribing === "pro_yearly" ? <FontAwesomeIcon icon={faSpinner} spin /> : "Subscribe yearly"}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {isPaidTier && (plan === "essential" || plan === "pro" || userType === "paid" || userType === "lifetime") && (
            <Card className="squircle shadow-md">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  To cancel or change your subscription, contact support or use the option in your payment provider.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAccounts;
